import { useState, useEffect, useMemo, useRef } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  Upload, Plus, Trash2, FileText, FileImage, FileSpreadsheet, Sparkles,
  Search, ChevronDown, X, AlertCircle, CheckCircle2, Loader2, BookMarked, Zap, RotateCw,
} from "lucide-react";
import RecurringManager from "@/pages/RecurringManager";

const STATUS_PILLS = {
  imported:    { label: "Imported",    cls: "bg-[#FDFBF7] border-[#C4A252] text-[#8B1C22]" },
  categorized: { label: "Categorized", cls: "bg-white border-[#15803D] text-[#15803D]" },
  reconciled:  { label: "Reconciled",  cls: "bg-[#0A0A0A] border-[#0A0A0A] text-white" },
  flagged:     { label: "Flagged",     cls: "bg-white border-[#8B1C22] text-[#8B1C22]" },
};

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* small inline modal */
const Modal = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white max-w-xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* Rules Manager Modal */
const RulesManager = ({ bookId, accounts, lang, onClose, onChanged }) => {
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newPattern, setNewPattern] = useState("");
  const [newType, setNewType] = useState("contains");
  const [newCat, setNewCat] = useState("");
  const incomeExpense = useMemo(() => accounts.filter((a) => !a.is_header && ["income", "cogs", "expense"].includes(a.type)), [accounts]);
  const accLabel = (code) => {
    const a = accounts.find((x) => x.code === code);
    if (!a) return code;
    return `${a.code} — ${lang === "es" && a.name_es ? a.name_es : a.name}`;
  };

  const load = async () => {
    const { data } = await api.get(`/back-office/books/${bookId}/rules`);
    setRules(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createRule = async (e) => {
    e.preventDefault();
    if (!newPattern.trim() || !newCat) return;
    setBusy(true);
    try {
      await api.post(`/back-office/books/${bookId}/rules`, {
        pattern: newPattern.trim(),
        match_type: newType,
        category_account_code: newCat,
      });
      toast.success("Rule created");
      setNewPattern(""); setNewCat("");
      load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleRule = async (r) => {
    await api.patch(`/back-office/books/${bookId}/rules/${r.id}`, { is_active: !r.is_active });
    load(); onChanged?.();
  };

  const deleteRule = async (r) => {
    if (!window.confirm(`Delete rule "${r.pattern}"?`)) return;
    await api.delete(`/back-office/books/${bookId}/rules/${r.id}`);
    load(); onChanged?.();
  };

  const applyToPast = async (r) => {
    if (!window.confirm(`Apply "${r.pattern}" → ${accLabel(r.category_account_code)} to all matching past transactions?`)) return;
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/rules/${r.id}/apply-to-past`);
      toast.success(`${data.applied} transaction(s) updated`);
      load(); onChanged?.();
    } catch (err) { toast.error("Failed to apply"); }
  };

  return (
    <div data-testid="rules-manager">
      <form onSubmit={createRule} className="border border-[#E5E5E5] p-4 bg-[#FDFBF7] space-y-3 mb-6" data-testid="new-rule-form">
        <div className="rn3-eyebrow">{lang === "es" ? "Nueva Regla" : "New Rule"}</div>
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-12 md:col-span-3">
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">Match</label>
            <select value={newType} onChange={(e) => setNewType(e.target.value)} className="rn3-input text-sm" data-testid="rule-type">
              <option value="contains">contains</option>
              <option value="starts_with">starts with</option>
              <option value="regex">regex</option>
            </select>
          </div>
          <div className="col-span-12 md:col-span-4">
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">Pattern</label>
            <input value={newPattern} onChange={(e) => setNewPattern(e.target.value)} placeholder="STARBUCKS" className="rn3-input text-sm" data-testid="rule-pattern" />
          </div>
          <div className="col-span-12 md:col-span-4">
            <label className="text-[10px] uppercase tracking-widest text-[#737373]">→ Category</label>
            <select value={newCat} onChange={(e) => setNewCat(e.target.value)} className="rn3-input text-sm" data-testid="rule-cat">
              <option value="">— select —</option>
              {incomeExpense.map((a) => <option key={a.code} value={a.code}>{accLabel(a.code)}</option>)}
            </select>
          </div>
          <div className="col-span-12 md:col-span-1">
            <button disabled={busy || !newPattern || !newCat} className="rn3-btn-primary w-full justify-center" data-testid="rule-add"><Plus className="w-4 h-4" /></button>
          </div>
        </div>
      </form>

      <div className="text-xs uppercase tracking-widest text-[#737373] mb-3">{rules.length} {lang === "es" ? "reglas" : "rules"}</div>
      {rules.length === 0 && (
        <div className="text-sm text-[#737373] text-center py-8">
          <BookMarked className="w-8 h-8 mx-auto mb-2 text-[#C4A252]" />
          {lang === "es" ? "Sin reglas aún. Crea una arriba o desde una transacción." : "No rules yet. Create one above or from any transaction."}
        </div>
      )}
      <div className="space-y-2">
        {rules.map((r) => (
          <div key={r.id} className={`border ${r.is_active ? "border-[#E5E5E5]" : "border-[#F3EFE9] opacity-60"} bg-white px-4 py-3 flex items-center gap-3 flex-wrap`} data-testid={`rule-row-${r.id}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#737373] w-16 shrink-0">{r.match_type.replace("_", " ")}</div>
            <code className="text-sm bg-[#FDFBF7] border border-[#E5E5E5] px-2 py-1 font-mono">{r.pattern}</code>
            <span className="text-[#C4A252]">→</span>
            <div className="text-sm flex-1 min-w-[180px]">{accLabel(r.category_account_code)}</div>
            <div className="text-xs text-[#737373]">{r.hit_count || 0} {lang === "es" ? "usos" : "uses"}</div>
            <button onClick={() => applyToPast(r)} className="text-xs text-[#8B1C22] hover:underline inline-flex items-center gap-1" data-testid={`rule-apply-${r.id}`}>
              <Zap className="w-3 h-3" /> {lang === "es" ? "Aplicar a pasadas" : "Apply to past"}
            </button>
            <button onClick={() => toggleRule(r)} className="text-xs text-[#737373] hover:text-[#0A0A0A]" data-testid={`rule-toggle-${r.id}`}>
              {r.is_active ? (lang === "es" ? "Desactivar" : "Disable") : (lang === "es" ? "Activar" : "Enable")}
            </button>
            <button onClick={() => deleteRule(r)} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`rule-delete-${r.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Statement import widget */
const ImportPanel = ({ bookId, accounts, lang, onDone }) => {
  const bankAccounts = accounts.filter((a) => !a.is_header && a.is_active && (a.subtype === "bank" || a.code.startsWith("10")));
  const [cashCode, setCashCode] = useState(bankAccounts[0]?.code || "1010");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("cash_account_code", cashCode);
      const { data } = await api.post(`/back-office/books/${bookId}/transactions/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`${data.count} transactions imported (${data.source.toUpperCase()})`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const ext = file?.name?.split(".").pop()?.toLowerCase();
  const Icon = ext === "csv" ? FileSpreadsheet : (["png","jpg","jpeg","webp"].includes(ext) ? FileImage : FileText);

  return (
    <div className="rn3-card" data-testid="import-panel">
      <div className="flex items-center gap-2 rn3-eyebrow"><Sparkles className="w-4 h-4" /> {lang === "es" ? "Importar Estado de Cuenta" : "Import Bank Statement"}</div>
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div className="md:col-span-1">
          <label className="text-xs uppercase tracking-widest text-[#737373] block mb-2">{lang === "es" ? "Cuenta de Banco" : "Bank Account"}</label>
          <select value={cashCode} onChange={(e) => setCashCode(e.target.value)} className="rn3-input" data-testid="import-bank-account">
            {bankAccounts.length === 0 && <option value="1010">1010 — Cash & Bank</option>}
            {bankAccounts.map((a) => (
              <option key={a.code} value={a.code}>{a.code} — {lang === "es" && a.name_es ? a.name_es : a.name}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-widest text-[#737373] block mb-2">{lang === "es" ? "Archivo (CSV, PDF, PNG, JPG)" : "File (CSV, PDF, PNG, JPG)"}</label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.pdf,.png,.jpg,.jpeg,.webp,application/pdf,text/csv,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block text-sm text-[#404040] file:mr-3 file:py-2 file:px-4 file:border file:border-[#C4A252] file:bg-transparent file:rounded-none file:text-[#0A0A0A] hover:file:bg-[#FDFBF7]"
              data-testid="import-file"
            />
            <button onClick={upload} disabled={!file || busy} className="rn3-btn-primary" data-testid="import-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy ? (lang === "es" ? "Procesando…" : "Processing…") : (lang === "es" ? "Importar" : "Import")}
            </button>
          </div>
          {file && (
            <div className="mt-3 text-xs text-[#737373] flex items-center gap-2">
              <Icon className="w-4 h-4 text-[#C4A252]" /> {file.name} · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-[#737373] mt-4">
        {lang === "es"
          ? "CSV se procesa al instante. PDF e imágenes usan Claude IA para extraer transacciones automáticamente."
          : "CSV imports instantly. PDF & image statements use Claude AI to extract transactions automatically."}
      </p>
    </div>
  );
};

/* Manual transaction modal */
const NewTxnForm = ({ bookId, accounts, onCancel, onCreated, lang }) => {
  const bankAccounts = accounts.filter((a) => !a.is_header && a.is_active);
  const incomeExpense = accounts.filter((a) => !a.is_header && a.is_active && ["income", "cogs", "expense"].includes(a.type));
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: "",
    amount: 0,
    cash_account_code: bankAccounts.find((a) => a.code === "1010")?.code || bankAccounts[0]?.code || "1010",
    category_account_code: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/back-office/books/${bookId}/transactions`, {
        ...form,
        amount: parseFloat(form.amount),
        category_account_code: form.category_account_code || null,
      });
      toast.success("Transaction added");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="manual-txn-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">Date</label>
          <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rn3-input" data-testid="mt-date" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">Amount</label>
          <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rn3-input" placeholder="Negative = expense" data-testid="mt-amount" />
        </div>
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">Description</label>
        <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rn3-input" data-testid="mt-desc" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-2">Bank / Cash</label>
          <select value={form.cash_account_code} onChange={(e) => setForm({ ...form, cash_account_code: e.target.value })} className="rn3-input" data-testid="mt-cash">
            {bankAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">Category</label>
          <select value={form.category_account_code} onChange={(e) => setForm({ ...form, category_account_code: e.target.value })} className="rn3-input" data-testid="mt-cat">
            <option value="">— Uncategorized —</option>
            {incomeExpense.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="rn3-eyebrow block mb-2">Notes</label>
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input" data-testid="mt-notes" />
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="mt-submit">{busy ? "…" : "Add Transaction"}</button>
      </div>
    </form>
  );
};

/* Main Transactions Tab */
const TransactionsTab = ({ book, accounts, lang }) => {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [accFilter, setAccFilter] = useState("");
  const [showRules, setShowRules] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [pendingRule, setPendingRule] = useState(null); // {txn, code}

  const refresh = async () => {
    try {
      const { data } = await api.get(`/back-office/books/${book.id}/transactions`);
      setTxns(data);
    } catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [book.id]);

  const saveRuleFromTxn = async (txn, code, patternOverride) => {
    try {
      const body = { transaction_id: txn.id, category_account_code: code, match_type: "contains" };
      if (patternOverride) body.pattern_override = patternOverride;
      const { data } = await api.post(`/back-office/books/${book.id}/rules/from-transaction`, body);
      toast.success(`Rule saved: "${data.pattern}" → ${data.category_account_code}`);
      setPendingRule(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rule");
    }
  };

  const incomeExpense = useMemo(
    () => accounts.filter((a) => !a.is_header && ["income", "cogs", "expense"].includes(a.type)),
    [accounts]
  );

  const accNameByCode = useMemo(() => {
    const m = {};
    for (const a of accounts) m[a.code] = lang === "es" && a.name_es ? a.name_es : a.name;
    return m;
  }, [accounts, lang]);

  const filtered = useMemo(() => {
    return txns.filter((t) => {
      if (filter !== "all" && t.status !== filter) return false;
      if (accFilter && t.cash_account_code !== accFilter) return false;
      if (q) {
        const lq = q.toLowerCase();
        const cat = t.category_account_code ? (accNameByCode[t.category_account_code] || "").toLowerCase() : "";
        if (!t.description.toLowerCase().includes(lq) && !cat.includes(lq) && !String(t.amount).includes(lq)) return false;
      }
      return true;
    });
  }, [txns, filter, accFilter, q, accNameByCode]);

  const totals = useMemo(() => {
    let inflow = 0, outflow = 0, uncat = 0;
    for (const t of filtered) {
      if (t.amount > 0) inflow += t.amount; else outflow += t.amount;
      if (!t.category_account_code) uncat++;
    }
    return { inflow, outflow, net: inflow + outflow, uncat, count: filtered.length };
  }, [filtered]);

  const updateCategory = async (t, code) => {
    try {
      const { data } = await api.patch(`/back-office/books/${book.id}/transactions/${t.id}`, { category_account_code: code || null });
      setTxns((arr) => arr.map((x) => (x.id === t.id ? data : x)));
      // offer to save as rule (only on manual confirms with a non-empty code that wasn't already from a rule)
      if (code && data.category_source !== "rule") {
        setPendingRule({ txn: data, code });
      }
    } catch { toast.error("Failed to update"); }
  };

  const updateStatus = async (t, status) => {
    try {
      const { data } = await api.patch(`/back-office/books/${book.id}/transactions/${t.id}`, { status });
      setTxns((arr) => arr.map((x) => (x.id === t.id ? data : x)));
    } catch { toast.error("Failed"); }
  };

  const removeTxn = async (t) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await api.delete(`/back-office/books/${book.id}/transactions/${t.id}`);
      setTxns((arr) => arr.filter((x) => x.id !== t.id));
    } catch { toast.error("Failed"); }
  };

  const bankAccounts = accounts.filter((a) => !a.is_header && (a.subtype === "bank" || a.code.startsWith("10")));

  return (
    <div className="space-y-8" data-testid="txns-tab">
      <ImportPanel bookId={book.id} accounts={accounts} lang={lang} onDone={refresh} />

      {/* totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="txns-totals">
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#737373]">{lang === "es" ? "Transacciones" : "Transactions"}</div>
          <div className="font-serif-display text-3xl mt-2">{totals.count}</div>
        </div>
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#15803D]">{lang === "es" ? "Ingresos" : "Inflow"}</div>
          <div className="font-serif-display text-3xl mt-2 text-[#0A0A0A]">{fmt(totals.inflow)}</div>
        </div>
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#8B1C22]">{lang === "es" ? "Gastos" : "Outflow"}</div>
          <div className="font-serif-display text-3xl mt-2 text-[#0A0A0A]">{fmt(totals.outflow)}</div>
        </div>
        <div className="rn3-card">
          <div className="text-xs uppercase tracking-widest text-[#C4A252]">{lang === "es" ? "Sin Categoría" : "Uncategorized"}</div>
          <div className="font-serif-display text-3xl mt-2 text-[#0A0A0A]">{totals.uncat}</div>
        </div>
      </div>

      {/* filters & manual add */}
      <div className="flex flex-wrap items-center gap-3" data-testid="txns-filters">
        <div className="flex items-center gap-2 px-3 py-2 border border-[#E5E5E5]">
          <Search className="w-3.5 h-3.5 text-[#737373]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "es" ? "Buscar…" : "Search…"} className="bg-transparent outline-none text-sm w-56" data-testid="txns-search" />
        </div>
        <select value={accFilter} onChange={(e) => setAccFilter(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-sm" data-testid="txns-account-filter">
          <option value="">{lang === "es" ? "Todas las cuentas" : "All bank accounts"}</option>
          {bankAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
        </select>
        <div className="flex gap-1">
          {[["all", lang==="es"?"Todas":"All"], ["imported", lang==="es"?"Importadas":"Imported"], ["categorized", lang==="es"?"Categorizadas":"Categorized"], ["reconciled", lang==="es"?"Conciliadas":"Reconciled"], ["flagged", lang==="es"?"Marcadas":"Flagged"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`txns-filter-${k}`}
              className={`px-3 py-2 text-xs uppercase tracking-wider border ${filter===k?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5] hover:border-[#C4A252]"}`}
            >{label}</button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowRecurring(true)} className="rn3-btn-gold" data-testid="txns-recurring-btn">
            <RotateCw className="w-4 h-4" /> {lang === "es" ? "Programadas" : "Recurring"}
          </button>
          <button onClick={() => setShowRules(true)} className="rn3-btn-gold" data-testid="txns-rules-btn">
            <BookMarked className="w-4 h-4" /> {lang === "es" ? "Reglas" : "Rules"}
          </button>
          <button onClick={() => setShowManual(true)} className="rn3-btn-gold" data-testid="txns-manual-add">
            <Plus className="w-4 h-4" /> {lang === "es" ? "Manual" : "Manual"}
          </button>
        </div>
      </div>

      {/* Pending rule prompt */}
      {pendingRule && (
        <div className="border border-[#C4A252] bg-[#FDFBF7] px-4 py-3 flex flex-wrap items-center gap-3" data-testid="pending-rule-banner">
          <Sparkles className="w-4 h-4 text-[#C4A252]" />
          <div className="text-sm flex-1 min-w-[200px]">
            {lang === "es" ? (
              <>Siempre categorizar como esta cuenta cuando la descripción contenga <code className="bg-white border border-[#E5E5E5] px-1.5 py-0.5 font-mono text-xs">{(pendingRule.txn.description.split(/\s+/)[0] || "").toUpperCase()}</code>?</>
            ) : (
              <>Always categorize this account when description contains <code className="bg-white border border-[#E5E5E5] px-1.5 py-0.5 font-mono text-xs">{(pendingRule.txn.description.split(/\s+/)[0] || "").toUpperCase()}</code>?</>
            )}
          </div>
          <button onClick={() => saveRuleFromTxn(pendingRule.txn, pendingRule.code)} className="rn3-btn-primary text-xs" data-testid="pending-rule-save">
            {lang === "es" ? "Sí, guardar regla" : "Yes, save rule"}
          </button>
          <button onClick={() => setPendingRule(null)} className="text-xs text-[#737373] hover:text-[#0A0A0A] px-2" data-testid="pending-rule-dismiss">
            {lang === "es" ? "Ahora no" : "Not now"}
          </button>
        </div>
      )}

      {/* table */}
      <div className="border border-[#E5E5E5] overflow-x-auto" data-testid="txns-table">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
            <tr>
              <th className="py-3 px-3 w-28">{lang==="es"?"Fecha":"Date"}</th>
              <th className="py-3 px-3">{lang==="es"?"Descripción":"Description"}</th>
              <th className="py-3 px-3 text-right w-28">{lang==="es"?"Monto":"Amount"}</th>
              <th className="py-3 px-3 w-64">{lang==="es"?"Categoría":"Category"}</th>
              <th className="py-3 px-3 w-32">{lang==="es"?"Estado":"Status"}</th>
              <th className="py-3 px-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="6" className="py-8 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>)}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="6" className="py-12 text-center text-[#737373]">
                <FileText className="w-8 h-8 mx-auto mb-2 text-[#C4A252]" />
                {lang === "es" ? "Sin transacciones. Importa un estado de cuenta arriba." : "No transactions yet. Import a statement above to get started."}
              </td></tr>
            )}
            {filtered.map((t) => {
              const pill = STATUS_PILLS[t.status] || STATUS_PILLS.imported;
              const needsAttention = !t.category_account_code;
              return (
                <tr key={t.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7]" data-testid={`txn-row-${t.id}`}>
                  <td className="py-2 px-3 whitespace-nowrap">{t.date}</td>
                  <td className="py-2 px-3">
                    <div className="text-[#0A0A0A]">{t.description}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-0.5">
                      {t.source} {t.source_file ? `· ${t.source_file}` : ""}
                    </div>
                  </td>
                  <td className={`py-2 px-3 text-right tabular-nums ${t.amount < 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(t.amount)}</td>
                  <td className="py-2 px-3">
                    <select
                      value={t.category_account_code || ""}
                      onChange={(e) => updateCategory(t, e.target.value)}
                      className={`w-full border px-2 py-1 text-xs ${needsAttention ? "border-[#C4A252] bg-[#FDFBF7]" : "border-[#E5E5E5]"}`}
                      data-testid={`txn-cat-${t.id}`}
                    >
                      <option value="">— {lang === "es" ? "Sin categoría" : "Uncategorized"} —</option>
                      {incomeExpense.map((a) => (
                        <option key={a.code} value={a.code}>{a.code} — {lang === "es" && a.name_es ? a.name_es : a.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <select value={t.status} onChange={(e) => updateStatus(t, e.target.value)} className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${pill.cls}`} data-testid={`txn-status-${t.id}`}>
                      <option value="imported">Imported</option>
                      <option value="categorized">Categorized</option>
                      <option value="reconciled">Reconciled</option>
                      <option value="flagged">Flagged</option>
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <button onClick={() => removeTxn(t)} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`txn-delete-${t.id}`} title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={showManual} onClose={() => setShowManual(false)} title={lang === "es" ? "Agregar Transacción" : "Add Transaction"} testid="manual-txn-modal">
        <NewTxnForm bookId={book.id} accounts={accounts} lang={lang} onCancel={() => setShowManual(false)} onCreated={() => { setShowManual(false); refresh(); }} />
      </Modal>

      <Modal open={showRules} onClose={() => setShowRules(false)} title={lang === "es" ? "Reglas de Categorización" : "Categorization Rules"} testid="rules-modal">
        <RulesManager bookId={book.id} accounts={accounts} lang={lang} onClose={() => setShowRules(false)} onChanged={refresh} />
      </Modal>

      <Modal open={showRecurring} onClose={() => setShowRecurring(false)} title={lang === "es" ? "Transacciones Programadas" : "Recurring Transactions"} testid="recurring-modal">
        <RecurringManager bookId={book.id} accounts={accounts} lang={lang} onChanged={refresh} />
      </Modal>
    </div>
  );
};

export default TransactionsTab;
